import React, { RefObject } from 'react';
import { ChartCard } from '@/components/visuals/ChartCard';
import type { ChartDefinition } from './registry';

const CALCULATION_BASIS_HEADING_IDS = new Set(['units_per_day_histogram', 'planned_units_s_curve']);

export interface OverviewFocusProps {
  chart: ChartDefinition;
  renderChart: (chartId: string, height: number | string) => React.ReactNode;
  /** Optional content above the chart (e.g. chart controls) */
  aboveChart?: React.ReactNode;
  /** Optional content below the chart + table (e.g. expandable Method section) */
  belowChart?: React.ReactNode;
  /** Optional ref attached to the chart wrapper (for export: find canvas inside) */
  chartContainerRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Focus mode: single chart rendered full-width at larger height.
 * Order: aboveChart (controls) → chart + table → belowChart (e.g. Method).
 * Title/subtitle are owned by the parent PageHeader. Drill-through via chart/list click.
 */
export const OverviewFocus: React.FC<OverviewFocusProps> = ({
  chart,
  renderChart,
  aboveChart,
  belowChart,
  chartContainerRef,
}) => {
  const controlsHeading = CALCULATION_BASIS_HEADING_IDS.has(chart.id) ? 'Calculation basis' : 'Chart options';

  return (
    <ChartCard
      title={chart.title}
      subtitle={chart.description}
      variant="focus"
      hideHeader
    >
      {aboveChart != null && (
        <div className="shrink-0 pb-4 mb-4 border-b border-border" data-chart-controls>
          <div className="flex items-center gap-2 mb-2 text-xs font-medium text-muted-foreground">
            {controlsHeading}
          </div>
          {aboveChart}
        </div>
      )}
      <div ref={chartContainerRef} className="flex-1 min-h-0" data-chart-export-container>
        {renderChart(chart.id, chart.focusHeight)}
      </div>
      {belowChart}
    </ChartCard>
  );
};
